import type { ActivatedRoute, NavigationExtras, ParamMap, Params } from '@angular/router';

/** A paginated list's page and page size. */
export interface ListPagination {
  page: number;
  pageSize: number;
}

/** What a list starts with: its page size, and the sizes its page-size dropdown offers. */
export interface ListPaginationDefaults {
  pageSize: number;
  pageSizeOptions: number[];
}

/**
 * The pagination a routed list's URL asks for. Anything missing or invalid — `page=abc`, `page=0`, `page=-2`, a size
 * the dropdown does not offer — falls back to the list's default.
 */
export function readListPagination(params: Pick<ParamMap, 'get'>, defaults: ListPaginationDefaults): ListPagination {
  const page = parsePositiveInteger(params.get('page'));
  const pageSize = parsePositiveInteger(params.get('pageSize'));

  return {
    page: page ?? 1,
    pageSize: pageSize !== null && defaults.pageSizeOptions.includes(pageSize) ? pageSize : defaults.pageSize
  };
}

/**
 * The query params for `pagination`. A default value is null, which a 'merge' navigation drops, so page 1 at the
 * default size stays the plain list URL.
 */
export function toListPaginationQueryParams(pagination: ListPagination, defaults: ListPaginationDefaults): Params {
  return {
    page: pagination.page === 1 ? null : pagination.page,
    pageSize: pagination.pageSize === defaults.pageSize ? null : pagination.pageSize
  };
}

/**
 * Writes a list's state into its own URL: merged, so other params stay, and replacing the history entry, so Back
 * leaves the list instead of stepping through every page it showed.
 */
export function listQueryParamsNavigation(route: ActivatedRoute, queryParams: Params): NavigationExtras {
  return { relativeTo: route, queryParams, queryParamsHandling: 'merge', replaceUrl: true };
}

/**
 * The page to move to when `pagination` came back empty although the list has items — a page kept in the URL that no
 * longer exists, which would otherwise show the list's empty state. Null when the page is fine.
 */
export function lastPageIfOutOfRange(
  pagination: ListPagination,
  totalItems: number,
  itemsOnPage: number
): number | null {
  if (itemsOnPage > 0 || pagination.page <= 1) {
    return null;
  }

  const lastPage = Math.max(1, Math.ceil(totalItems / pagination.pageSize));
  // Only ever moves down, so a server that reports items it then does not return cannot cause a redirect loop.
  return lastPage < pagination.page ? lastPage : null;
}

function parsePositiveInteger(value: string | null): number | null {
  // Digits only, so '-2', '1.5', '1e3' and ' 3' are rejected rather than coerced.
  if (value === null || !/^\d+$/.test(value)) {
    return null;
  }

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}
